import TR from 'utils/TR';
import React from 'react';
import T from 'utils/T';

const refFunc = (outerRef, elemRef, id) => (ref) => {
  if (outerRef) outerRef.current = ref;
  elemRef.current[id] = ref;
};

export const defaultPasswordValidatingOptions = (banned) => ({
  is: {
    required: { message: T('필수 입력 입니다.') },
    min_length: { condition: 10, message: T('길이가 {0}보다 짧습니다') },
    max_length: { condition: 100, message: T('길이가 너무 깁니다') },
  },
  has: {
    uppercase: { condition: 1, message: T('대문자를 반드시 포함해야 합니다') },
    lowercase: { condition: 1, message: T('소문자를 반드시 포함해야 합니다') },
    symbols: { condition: 1, message: T('특수문자를 반드시 포함해야 합니다') },
    digits: { condition: 1, message: T('숫자를 반드시 포함해야 합니다') },
    not: {
      oneOf: { condition: ['root', 'admin', ...(banned || []).filter(word => 3 < word.length)], message: T('{0} 단어를 포함해서는 안됩니다') },
      spaces: { condition: 1, message: T('공백이 들어가 있습니다') },
      repeat: { condition: 3, message: T('{0}가 {1}번이상 반복하고 있습니다') },
      continuous: { condition: 3, message: T('{0}처럼 연속적인 세 글자는 사용할 수 없습니다') },
    },
  },
});

export default function useForm(props) {
  const {
    defaultValues: values = {},
  } = props || {};

  const [defaultValues, setDefaultValues] = React.useState(() => ({ ...values }));
  const [updated, setUpdated] = React.useState(() => 0);

  const dataRef = React.useRef({ ...values });
  const elemRef = React.useRef({});
  const validateOptionsRef = React.useRef({});
  const [errors, setErrors] = React.useState({});

  const detachedRef = React.useRef(false);
  React.useEffect(() => {
    detachedRef.current = false;
    return () => {
      detachedRef.current = true;
      setDefaultValues(() => ({ ...values }));
      setUpdated(() => -1);
      dataRef.current = ({ ...values });
      elemRef.current = {};
      validateOptionsRef.current = {};
      setErrors(() => ({}));
    };
  }, []);

  const onValidate = React.useCallback((value, options) => {
    if (typeof options === 'function') {
      const result = options(dataRef.current);
      if (result) {
        return result;
      } else {
        return null;
      }
    }

    const {
      is,
      has,
    } = options || {};
    const {
      not: is_not,
      required, // 값이 없거나 비어있으면 에러
      pattern, // 패턴과 일치하는 부분이 있으면 에러,
      min, // 최소값보다 작을 경우 에러
      max, // 최대값보다 클 경우 에러
      min_length, // 최소 글자 갯수, n개 미만일 경우 에러
      max_length, // 최대 글자 갯수, n개 초과일 경우 에러
    } = is || {};
    const {
      pattern: not_pattern, // 패턴과 일치하는 부분이 없으면 에러
      min: not_min, // 최소값보다 작지 않을 경우 에러
      max: not_max, // 최고값보다 크지 않을 경우 에러
      min_length: not_min_length, // 최소 글자 갯수, n개 미만이 아닐 경우 에러
      max_length: not_max_length, // 최대 글자 갯수, n개 초과가 아닐 경우 에러
    } = is_not || {};
    const {
      not: has_not,
      letters, // 문자 갯수, n개 미만인 경우 에러
      uppercase, // 대문자 갯수, n개 미만인 경우 에러
      lowercase, // 소문자 갯수, n개 미만인 경우 에러
      digits, // 숫자 갯수, n개 미만인 경우 에러
      symbols, // 기호 갯수, n개 미만인 경우 에러
      spaces, // 공백 갯수, n개 미만인 경우 에러
      oneOf, // list에 있는 단어가 하나도 없으면 에러
    } = has || {};
    const {
      letters: not_letters, // 글자 갯수, n개 미만이 아닌 경우 에러
      uppercase: not_uppercase, // 대문자 갯수, n개 미만이 아닌 경우 에러
      lowercase: not_lowercase, // 소문자 갯수, n개 미만이 아닌 경우 에러
      digits: not_digits, // 숫자 갯수, n개 미만이 아닌 경우 에러
      symbols: not_symbols, // 기호 갯수, n개 미만이 아닌 경우 에러
      spaces: not_spaces, // 공백 갯수, n개 미만이 아닌 경우 에러
      oneOf: not_oneOf, // list에 있는 단어가 하나라도 있으면 에러
      repeat: not_repeat, // 반복되는 숫자, n개 연속인 경우 에러
      continuous: not_continuous, // 연속적인 숫자, n개 연속인 경우 에러
    } = has_not || {}

    if (required && (value === null || value === undefined || !value?.length)) return T(required?.message, true, value);
    if (min !== undefined && value < min) return T(min?.message, min?.condition, value);
    if (max !== undefined && max < value) return T(max?.message, max?.condition, value);
    if (not_min !== undefined && !(value < min)) return T(not_min?.message, not_min?.condition, value);
    if (not_max !== undefined && !(max < value)) return T(not_max?.message, not_max?.condition, value);
    if (min_length && value.length < min_length?.condition) return T(min_length?.message, min_length?.condition, value);
    if (max_length && max_length?.condition < value.length) return T(max_length?.message, max_length?.condition, value);
    if (not_min_length && !(value.length < not_min_length?.condition)) return T(not_min_length?.message, not_min_length?.condition, value);
    if (not_max_length && !(not_max_length?.condition < value.length)) return T(not_max_length?.message, not_max_length?.condition, value);

    if (pattern && pattern.condition.test(value)) return T(pattern?.message, pattern?.condition, value);
    if (not_pattern && !not_pattern.condition.test(value)) return T(not_pattern?.message, not_pattern?.condition, value);

    let count_letters = 0;
    let count_uppercase = 0;
    let count_lowercase = 0;
    let count_digits = 0;
    let count_symbols = 0;
    let count_spaces = 0;
    const hash_oneOf = {};
    const hash_not_oneOf = {};
    const hash_repeat = {};
    const array_continuous = [];

    if (typeof value === 'string') {

      const cs = value.split('');
      for (let i = 0; i < cs.length; ++i) {
        const c = cs[i];

        if (/[a-z]/.test(c)) {
          ++count_letters;
          ++count_lowercase;
        }
        if (/[A-Z]/.test(c)) {
          ++count_letters;
          ++count_uppercase;
        }
        if (/[0-9]/.test(c)) {
          ++count_digits;
        }
        if (/[!"#$%&'()*+,-./:;<=>?@\[\]^_`{|}~]/.test(c)) {
          ++count_symbols;
        }
        if (/\s/.test(c)) {
          ++count_spaces;
        }
        (oneOf?.condition || []).forEach(one => {
          if (0 <= value.indexOf(one)) {
            hash_oneOf[one] = true;
          }
        });
        (not_oneOf?.condition || []).forEach(one => {
          if (0 <= value.indexOf(one)) {
            hash_not_oneOf[one] = true;
          }
        });
        if (c in hash_repeat) {
          ++hash_repeat[c];
          if (not_repeat && not_repeat?.condition < hash_repeat[c]) {
            return T(not_repeat?.message, c, not_repeat.condition, value);
          }
        } else {
          Object.keys(hash_repeat).forEach(r => delete hash_repeat[r]);
          hash_repeat[c] = 1;
        }
        if (/[A-Za-z0-9]/.test(c)) {
          if (!array_continuous.length) {
            array_continuous.unshift(c);
          } else if (array_continuous.length === 1) {
            if (Math.abs(array_continuous[0].charCodeAt(0) - c.charCodeAt(0)) === 1) {
              array_continuous.unshift(c);
            } else {
              array_continuous.splice(0, array_continuous.length);
              array_continuous.unshift(c);
            }
          } else if (1 < array_continuous.length) {
            const da = array_continuous[0].charCodeAt(0) - array_continuous[1].charCodeAt(0);
            const dc = c.charCodeAt(0) - array_continuous[0].charCodeAt(0);
            if (da === dc) {
              array_continuous.unshift(c);
            } else {
              array_continuous.splice(0, array_continuous.length);
              array_continuous.unshift(c);
            }
            if (not_continuous && not_continuous?.condition <= array_continuous.length) {
              return T(not_continuous?.message, [...array_continuous].reverse().join(''), not_continuous?.condition, value);
            }
          }
        } else {
          array_continuous.splice(0, array_continuous.length);
        }
      }
    }

    if (letters && count_letters < letters?.condition) return T(letters?.message, letters?.condition, value);
    if (lowercase && count_lowercase < lowercase?.condition) return T(lowercase?.message, lowercase?.condition, value);
    if (uppercase && count_uppercase < uppercase?.condition) return T(uppercase?.message, uppercase?.condition, value);
    if (digits && count_digits < digits?.condition) return T(digits?.message, digits?.condition, value);
    if (symbols && count_symbols < symbols?.condition) return T(symbols?.message, symbols?.condition, value);
    if (spaces && count_spaces < spaces?.condition) return T(spaces?.message, spaces?.condition, value);
    if (not_letters && !(count_letters < not_letters?.condition)) return T(not_letters?.message, not_letters?.condition, value);
    if (not_uppercase && !(count_uppercase < not_uppercase?.condition)) return T(not_uppercase?.message, not_uppercase?.condition, value);
    if (not_lowercase && !(count_lowercase < not_lowercase?.condition)) return T(not_lowercase?.message, not_lowercase?.condition, value);
    if (not_digits && !(count_digits < not_digits?.condition)) return T(not_digits?.message, not_digits?.condition, value);
    if (not_symbols && !(count_symbols < not_symbols?.condition)) return T(not_symbols?.message, not_symbols?.condition, value);
    if (not_spaces && !(count_spaces < not_spaces?.condition)) return T(not_spaces?.message, not_spaces?.condition, value);

    if (oneOf && !Object.keys(hash_oneOf).length) {
      return T(oneOf?.message, oneOf?.condition);
    }
    if (not_oneOf && Object.keys(hash_not_oneOf).length) {
      return T(not_oneOf?.message, Object.keys(hash_not_oneOf)[0], value);
    }

    return null; // 에러 없음
  }, []);

  const validate = React.useCallback((id) => {
    const result = onValidate(dataRef.current[id], validateOptionsRef.current[id]);
    if (result !== null) {
      setErrors(errors => ({
        ...errors,
        [id]: result,
      }));
    } else {
      setErrors(({ ...errors }) => {
        delete errors[id];
        return errors;
      });
    }
  }, [onValidate]);
  const setValue = React.useCallback((id, value) => {
    if (id in elemRef.current) elemRef.current[id].value = value;

    dataRef.current = {
      ...dataRef.current,
      [id]: value,
    };
    const result = onValidate(value, validateOptionsRef.current[id]);
    if (result !== null) {
      setErrors(errors => ({
        ...errors,
        [id]: result,
      }));
    } else {
      setErrors(({ ...errors }) => {
        delete errors[id];
        return errors;
      });
    }
    setUpdated(u => u + 1);
  }, [onValidate]);
  const onInnerChange = React.useCallback((id, callback) => (e) => {
    const { value } = e.target;
    dataRef.current = {
      ...dataRef.current,
      [id]: e.target.value,
    };
    const result = onValidate(value, validateOptionsRef.current[id]);
    if (result !== null) {
      setErrors(errors => ({
        ...errors,
        [id]: result,
      }));
    } else {
      setErrors(({ ...errors }) => {
        delete errors[id];
        return errors;
      });
    }
    callback && callback(e);
    setUpdated(u => u + 1);
  }, [onValidate]);
  const onInnerBlur = React.useCallback((id, callback) => (e) => {
    const { value } = e.target;
    const result = onValidate(value, validateOptionsRef.current[id]);
    if (result !== null) {
      setErrors(errors => ({
        ...errors,
        [id]: result,
      }))
    } else {
      setErrors(({ ...errors }) => {
        delete errors[id];
        return errors;
      });
    }
    callback && callback(e);
    setUpdated(u => u + 1);
  }, [onValidate]);
  const register = React.useCallback((id, options) => {
    const {
      ref: outerRef,
      name,
      disabled,
      defaultValue,
      validate,
      onChange,
      onBlur,
      ...rest
    } = options;
    if (validate) {
      validateOptionsRef.current = {
        ...validateOptionsRef.current,
        [id]: validate,
      };
    }
    if (!(id in dataRef.current)) {
      dataRef.current = {
        ...dataRef.current,
        [id]: defaultValue || defaultValues[id],
      };
    }
    return {
      ref: refFunc(outerRef, elemRef, id),
      id,
      name: name || id,
      disabled,
      ...(!(id in dataRef.current) && defaultValue ? { defaultValue } : {}),
      ...(id in dataRef.current ? { value: dataRef.current[id] } : {}),
      onChange: onInnerChange(id, onChange),
      onBlur: onInnerBlur(id, onBlur),
      ...rest
    };
  }, [defaultValues, onInnerBlur, onInnerChange]);

  const onSubmit = React.useCallback((callback) => (e) => {
    const keys = Object.keys(dataRef.current);
    const success = keys.every(key => {
      const result = onValidate(dataRef.current[key], validateOptionsRef.current[key]);
      if (result !== null) {
        setErrors(errors => ({
          ...errors,
          [key]: result,
        }));
        return false;
      } else {
        setErrors(({ ...errors }) => {
          delete errors[key];
          return errors;
        });
        return true;
      }
    });
    if (success) {
      const parameters = { ...dataRef.current };
      requestAnimationFrame(() => {
        callback && callback(parameters);
      });
    }
    e.preventDefault();
    e.stopPropagation();
    setUpdated(u => u + 1);
  }, [onValidate]);

  return {
    data: dataRef.current,
    errors,
    register,
    validate,
    onSubmit,
    setValue,
    setErrors,
  };
}
